import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { POSTS } from "@/data/blogPosts";
import { ArrowRight, Clock, Tag } from "@phosphor-icons/react";

function formatDateFR(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function Blog() {
  const featured = POSTS[0];
  const others = POSTS.slice(1);

  return (
    <Layout>
      <SEO
        title="Blog · Guides et astuces pour retrouver un doudou perdu"
        description="Nos guides pratiques pour ne plus jamais perdre le doudou de votre enfant : crèche, train, parc, prévention. Conseils testés par des milliers de parents."
      />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <header className="text-center mb-14" data-testid="blog-header">
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-orange-500">
            Le blog Doudou Alert
          </span>
          <h1 className="font-display text-4xl sm:text-5xl font-black text-stone-800 mt-3 leading-tight">
            Guides & astuces pour <span className="text-orange-500">retrouver</span>
            <br className="hidden sm:block" /> un doudou perdu
          </h1>
          <p className="text-stone-600 mt-4 max-w-2xl mx-auto text-lg">
            Des conseils concrets, testés par des milliers de parents, pour agir
            vite quand un doudou disparaît — et surtout pour éviter que ça se
            reproduise.
          </p>
        </header>

        {/* Featured article */}
        {featured && (
          <Link
            to={`/blog/${featured.slug}`}
            className="group block rounded-3xl bg-gradient-to-br from-orange-500 to-rose-500 text-white p-8 md:p-12 mb-10 shadow-xl shadow-orange-200/50 hover:shadow-2xl hover:shadow-orange-300/60 transition-all"
            data-testid={`blog-featured-${featured.slug}`}
          >
            <div className="flex flex-wrap items-center gap-3 text-white/90 text-xs font-bold uppercase tracking-widest mb-4">
              <span className="px-3 py-1 rounded-full bg-white/20 backdrop-blur">
                À la une
              </span>
              <span>{featured.category}</span>
              <span>·</span>
              <span className="flex items-center gap-1">
                <Clock size={14} weight="bold" /> {featured.readingMinutes} min
              </span>
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-black leading-tight">
              {featured.title}
            </h2>
            <p className="mt-4 text-white/90 leading-relaxed max-w-3xl">
              {featured.excerpt}
            </p>
            <span className="inline-flex items-center gap-2 mt-6 font-bold group-hover:translate-x-1 transition-transform">
              Lire l'article <ArrowRight size={18} weight="bold" />
            </span>
          </Link>
        )}

        {/* Other articles */}
        <div className="grid md:grid-cols-2 gap-6">
          {others.map((p) => (
            <Link
              key={p.slug}
              to={`/blog/${p.slug}`}
              className="group block rounded-3xl bg-white border border-rose-100 p-7 hover:border-orange-200 hover:shadow-xl hover:shadow-orange-100 transition-all"
              data-testid={`blog-card-${p.slug}`}
            >
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-orange-500 mb-3">
                <Tag size={14} weight="bold" />
                <span>{p.category}</span>
                <span className="text-stone-300">·</span>
                <span className="text-stone-500 flex items-center gap-1">
                  <Clock size={12} weight="bold" /> {p.readingMinutes} min
                </span>
              </div>
              <h3 className="font-display text-xl font-black text-stone-800 leading-snug group-hover:text-orange-600 transition-colors">
                {p.title}
              </h3>
              <p className="mt-3 text-stone-600 leading-relaxed line-clamp-3">
                {p.excerpt}
              </p>
              <span className="inline-flex items-center gap-2 mt-4 font-bold text-orange-600 group-hover:translate-x-1 transition-transform">
                Lire <ArrowRight size={16} weight="bold" />
              </span>
            </Link>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-16 text-center bg-gradient-to-br from-rose-50 to-orange-50 rounded-3xl p-10 border border-rose-100">
          <p className="text-stone-500 text-sm font-bold uppercase tracking-widest">
            Vous avez perdu un doudou ?
          </p>
          <p className="font-display text-2xl md:text-3xl font-black text-stone-800 mt-2">
            Créez une alerte en 2 minutes — c'est <span className="text-orange-500">gratuit</span>.
          </p>
          <Link to="/inscription">
            <button className="mt-6 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-full px-8 py-3 shadow-lg shadow-orange-500/30" data-testid="blog-cta-signup">
              Créer mon compte
            </button>
          </Link>
        </div>
      </div>
    </Layout>
  );
}
